import { format, parse } from "date-fns";
import { id } from "date-fns/locale";
import { cn, getWeekStartAndEndDates } from "src/lib/utils";
import { formatNumber } from "../numberingformat";
import { Card, CardContent } from "../shadcn/card";
import { useMediaQuery } from "src/lib/hooks";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../shadcn/table";
import { CircleDollarSign, Loader2Icon, TrashIcon } from "lucide-react";
import { Button } from "../shadcn/button";
import { useMutation } from "@tanstack/react-query";
import { instance2 } from "../utils/axiosInstance";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../shadcn/alert-dialog";

const formatDate = (date, dateFormat = "yyyy-MM-dd") =>
  format(date, dateFormat, { locale: id });

const useDeleteOrder = ({ order, refetchData }) =>
  useMutation({
    mutationKey: ["delete", { id: order.id }],
    mutationFn: async () => instance2.delete(`/order_draft_detail/${order.id}`),
    onError: ({ message }) =>
      toast.error(`Gagal menghapus order ${order.ingredient_name}: ${message}`),
    onSuccess: () => {
      toast.success(`Berhasil menghapus order ${order.ingredient_name}!`);
      refetchData();
    },
  });

export const OrderTambahanList = ({ date, orders, refetchData }) => {
  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const parsedDate = parse(date, "d MMMM yyyy", new Date(), { locale: id });
  const { startDate, endDate } = getWeekStartAndEndDates(parsedDate);
  const orderUrl = `/daftarpesanan?startDate=${formatDate(
    startDate,
  )}&endDate=${formatDate(endDate)}`;
  const totalPrice = orders.reduce(
    (totalPrice, order) => totalPrice + Number(order.price_total),
    0,
  );

  return (
    <div className="flex flex-col gap-0 w-full">
      <div className="flex items-center gap-4 w-full">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold my-2 whitespace-nowrap">
            {date}
          </h3>
          <p className="w-fit whitespace-nowrap text-gray-500 hidden md:block">
            Rp. {formatNumber(totalPrice)},-
          </p>
        </div>
        <hr className="w-full" />
        <p className="w-fit whitespace-nowrap text-gray-500 md:hidden">
          Rp. {formatNumber(totalPrice)},-
        </p>
      </div>

      <div className="grid grid-cols-1 space-y-1">
        {isDesktop ? (
          <OrdersTable
            orders={orders}
            orderUrl={orderUrl}
            refetchData={refetchData}
          />
        ) : (
          orders.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              orderUrl={orderUrl}
              refetchData={refetchData}
            />
          ))
        )}
      </div>
    </div>
  );
};

const OrdersTable = ({ orders, orderUrl, refetchData }) => {
  const columns = [
    { label: "Nama Bahan" },
    { label: "Kuantitas", className: "text-center" },
    { label: "Harga Satuan", className: "text-right" },
    { label: "Total Harga", className: "text-right" },
    { label: "Aksi", className: "text-right" },
  ];

  return (
    <div className="border overflow-hidden rounded-lg">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-100">
            {columns.map(column => (
              <TableHead
                key={column.label}
                className={cn(
                  "first-of-type:pl-4 last-of-type:pr-5",
                  column.className,
                )}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map(order => (
            <OrdersTableRow
              key={order.id}
              order={order}
              orderUrl={orderUrl}
              refetchData={refetchData}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const OrdersTableRow = ({ order, orderUrl, refetchData }) => {
  const { mutate: deleteOrder, isPending } = useDeleteOrder({
    order,
    refetchData,
  });

  return (
    <TableRow className="hover:bg-gray-50 hover:cursor-pointer first:pl-4 last:pr-4">
      <Link className="contents" to={orderUrl}>
        <TableCell className="pl-4">{order.ingredient_name}</TableCell>
        <TableCell className="lowercase text-center">
          {formatNumber(order.order_qty)} {order.uoq_code}
        </TableCell>
        <TableCell className="text-right">
          Rp. {formatNumber(order.price_unit)} /{" "}
          <span className="lowercase">{order.uoq_code}</span>
        </TableCell>
        <TableCell className="text-right">
          Rp. {formatNumber(order.price_total)}
        </TableCell>
      </Link>
      <TableCell key={`delete-${order.id}`} className="text-right pr-4">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={isPending} size="icon">
              {isPending ? (
                <Loader2Icon className="animate-spin" />
              ) : (
                <TrashIcon />
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Apakah Anda yakin?</AlertDialogTitle>
              <AlertDialogDescription>
                Tindakan ini akan menghapus pesanan tambahan untuk{" "}
                <strong>{order.ingredient_name}</strong>. Tindakan ini tidak
                bisa dibatalkan.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Batal</AlertDialogCancel>
              <AlertDialogAction onClick={() => deleteOrder(order.id)}>
                Hapus
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </TableCell>
    </TableRow>
  );
};

const OrderCard = ({ order, orderUrl, refetchData }) => {
  const { mutate: deleteOrder, isPending } = useDeleteOrder({
    order,
    refetchData,
  });

  return (
    <Card className="my-2 hover:bg-gray-100">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <Link to={orderUrl} className="flex-grow">
            <div className="flex justify-between items-start mb-2 gap-2">
              <h3 className="font-semibold text-base">
                {order.ingredient_name}
              </h3>
              <span className="text-xs bg-gray-200 text-gray-500 px-2 py-1 rounded-full whitespace-nowrap">
                {formatNumber(order.order_qty)}{" "}
                <span className="lowercase">{order.uoq_code}</span>
              </span>
            </div>
            <div className="flex justify-between items-start text-gray-500">
              <div className="flex items-center gap-1">
                <CircleDollarSign size={12} />
                <p className="text-xs">
                  Rp. {formatNumber(order.price_unit)} /{" "}
                  <span className="lowercase">{order.uoq_code}</span>
                </p>
              </div>
              <p className="text-xs">Rp. {formatNumber(order.price_total)}</p>
            </div>
          </Link>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                disabled={isPending}
                size="icon"
                className="shrink-0"
              >
                {isPending ? (
                  <Loader2Icon className="animate-spin" />
                ) : (
                  <TrashIcon />
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Apakah Anda yakin?</AlertDialogTitle>
                <AlertDialogDescription>
                  Tindakan ini akan menghapus pesanan tambahan untuk{" "}
                  <strong>{order.ingredient_name}</strong>. Tindakan ini tidak
                  bisa dibatalkan.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Batal</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteOrder(order.id)}>
                  Hapus
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
};
